import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Package, Upload, RefreshCw, Link2, Unlink, Search, ShoppingBag,
  AlertCircle, CheckCircle2, Clock, XCircle, Loader2,
  Scissors, Plus, ToggleLeft, ToggleRight, Pencil,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/operator/catalog')({
  component: ProductsAndServicesPage,
});

// ============================================================================
// Types
// ============================================================================

interface CatalogStatus {
  connected: boolean;
  catalog?: {
    id: string;
    businessId: string;
    metaCatalogId: string;
    metaBusinessId: string;
    syncStatus: string;
    lastSyncAt: string | null;
    productCount: number;
    createdAt: string;
    updatedAt: string;
  };
  recentProducts?: Product[];
  tokenExpired?: boolean;
}

interface ProductService {
  id: string;
  name: string;
  durationMinutes: number;
  priceCents: number;
  bufferMinutes: number;
  active: boolean;
}

interface Product {
  id: string;
  sku: string;
  localSku: string | null;
  name: string;
  description: string | null;
  price: string;
  priceCents: number;
  currency: string;
  availability: string;
  imageUrl: string | null;
  category: string | null;
  type?: 'SERVICE' | 'PRODUCT' | 'COMBO';
  serviceId?: string | null;
  service?: ProductService | null;
  syncStatus: string;
  lastSyncAt: string;
  updatedAt: string;
}

interface Service {
  id: string;
  businessId: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  priceCents: number;
  active: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: { bookings: number };
}

interface ServiceForm {
  name: string;
  description: string;
  durationMinutes: string;
  priceCents: string;
  active: boolean;
  displayOrder: string;
}

const EMPTY_FORM: ServiceForm = {
  name: '', description: '', durationMinutes: '30', priceCents: '0', active: true, displayOrder: '0',
};

// ============================================================================
// Helpers
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL || '';

function getHeaders() {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const formatPrice = (cents: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);

const formatDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

function availabilityBadge(availability: string) {
  const lower = availability.toLowerCase();
  if (lower === 'in stock') return { variant: 'default' as const, className: 'bg-green-100 text-green-800 hover:bg-green-100' };
  if (lower === 'out of stock') return { variant: 'destructive' as const, className: '' };
  if (lower === 'low stock') return { variant: 'outline' as const, className: 'border-amber-400 text-amber-700' };
  return { variant: 'secondary' as const, className: '' };
}

function syncBadge(status: string) {
  switch (status) {
    case 'SYNCED': return { icon: CheckCircle2, className: 'text-green-600' };
    case 'PENDING': return { icon: Clock, className: 'text-amber-600' };
    case 'FAILED': return { icon: XCircle, className: 'text-red-600' };
    default: return { icon: AlertCircle, className: 'text-gray-500' };
  }
}

// ============================================================================
// Services Tab
// ============================================================================

function ServicesTab({ businessId }: { businessId: string }) {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [form, setForm] = useState<ServiceForm>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchServices = useCallback(async () => {
    try {
      const params = new URLSearchParams({ businessId, pageSize: '100' });
      if (searchQuery) params.set('search', searchQuery);
      const res = await fetch(`${API_URL}/api/operator/services?${params}`, {
        credentials: 'include', headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setServices(data.data || []);
      setTotal(data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      setError(t('servicesMgmt.errors.loadFailed'));
      console.error('Services fetch error:', err);
    }
  }, [businessId, searchQuery, t]);

  useEffect(() => {
    setIsLoading(true);
    fetchServices().finally(() => setIsLoading(false));
  }, [fetchServices]);

  const openCreate = () => {
    setEditingService(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (service: Service) => {
    setEditingService(service);
    setForm({
      name: service.name,
      description: service.description || '',
      durationMinutes: String(service.durationMinutes),
      priceCents: String(service.priceCents),
      active: service.active,
      displayOrder: String(service.displayOrder),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);
    const name = form.name.trim();
    if (!name) { setFormError(t('servicesMgmt.validation.nameRequired')); return; }
    const durationMinutes = parseInt(form.durationMinutes);
    if (!durationMinutes || durationMinutes <= 0) {
      setFormError(t('servicesMgmt.validation.durationPositive'));
      return;
    }
    const priceCents = parseInt(form.priceCents);
    if (isNaN(priceCents) || priceCents < 0) {
      setFormError(t('servicesMgmt.validation.priceNonNegative'));
      return;
    }
    setIsSaving(true);
    try {
      const body = {
        businessId, name,
        description: form.description.trim() || null,
        durationMinutes, priceCents,
        active: form.active,
        displayOrder: parseInt(form.displayOrder) || 0,
      };
      const url = editingService
        ? `${API_URL}/api/operator/services/${editingService.id}`
        : `${API_URL}/api/operator/services`;
      const res = await fetch(url, {
        method: editingService ? 'PATCH' : 'POST',
        credentials: 'include', headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `${res.status}`);
      }
      setDialogOpen(false);
      await fetchServices();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to save service');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (service: Service) => {
    try {
      const res = await fetch(`${API_URL}/api/operator/services/${service.id}`, {
        method: 'PATCH', credentials: 'include', headers: getHeaders(),
        body: JSON.stringify({ businessId, active: !service.active }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      await fetchServices();
    } catch (err) { console.error('Toggle active error:', err); }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-md text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
            {t('common.close')}
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {t('servicesMgmt.serviceCount', { count: total })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('servicesMgmt.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="services-search"
                />
              </div>
              <Button onClick={openCreate} size="sm" data-testid="new-service-btn">
                <Plus className="h-4 w-4 mr-1" />
                {t('servicesMgmt.newService')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : services.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Scissors className="h-10 w-10 mb-3 opacity-40" />
              <p className="font-medium">{t('servicesMgmt.noServices')}</p>
              <p className="text-sm mt-1">{t('servicesMgmt.noServicesDesc')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="services-table">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">{t('servicesMgmt.columns.name')}</th>
                    <th className="py-2 pr-4 font-medium">{t('servicesMgmt.columns.duration')}</th>
                    <th className="py-2 pr-4 font-medium">{t('servicesMgmt.columns.price')}</th>
                    <th className="py-2 pr-4 font-medium">{t('servicesMgmt.columns.active')}</th>
                    <th className="py-2 pr-4 font-medium">{t('servicesMgmt.columns.bookings')}</th>
                    <th className="py-2 font-medium">{t('servicesMgmt.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc) => (
                    <tr key={svc.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-3 pr-4">
                        <div>
                          <span className="font-medium">{svc.name}</span>
                          {svc.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[250px]">{svc.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">{formatDuration(svc.durationMinutes)}</td>
                      <td className="py-3 pr-4 font-medium">{formatPrice(svc.priceCents)}</td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant={svc.active ? 'default' : 'secondary'}
                          className={svc.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-100'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-100'}
                        >
                          {svc.active ? t('servicesMgmt.active') : t('servicesMgmt.inactive')}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">{svc._count?.bookings ?? 0}</td>
                      <td className="py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(svc)}
                            title={t('servicesMgmt.editService')}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleActive(svc)}
                            title={svc.active ? t('servicesMgmt.deactivate') : t('servicesMgmt.activate')}>
                            {svc.active
                              ? <ToggleRight className="h-4 w-4 text-green-600" />
                              : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingService ? t('servicesMgmt.editService') : t('servicesMgmt.newService')}
            </DialogTitle>
            <DialogDescription>
              {editingService
                ? t('servicesMgmt.editDescription')
                : t('servicesMgmt.createDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {formError && (
              <div className="flex items-center gap-2 p-2 bg-red-50 text-red-700 rounded text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />{formError}
              </div>
            )}
            <div>
              <Label>{t('servicesMgmt.form.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('servicesMgmt.form.namePlaceholder')} className="mt-1"
                data-testid="service-name-input" />
            </div>
            <div>
              <Label>{t('servicesMgmt.form.description')}</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={t('servicesMgmt.form.descriptionPlaceholder')} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('servicesMgmt.form.duration')}</Label>
                <Input type="number" min="1" value={form.durationMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                  className="mt-1" data-testid="service-duration-input" />
              </div>
              <div>
                <Label>{t('servicesMgmt.form.price')}</Label>
                <Input type="number" min="0" value={form.priceCents}
                  onChange={(e) => setForm((f) => ({ ...f, priceCents: e.target.value }))}
                  className="mt-1" data-testid="service-price-input" />
                <p className="text-xs text-muted-foreground mt-1">{t('servicesMgmt.form.priceHint')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('servicesMgmt.form.displayOrder')}</Label>
                <Input type="number" min="0" value={form.displayOrder}
                  onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))} className="mt-1" />
              </div>
              <div className="flex items-end">
                <Button variant="outline" size="sm" className="w-full"
                  onClick={() => setForm((f) => ({ ...f, active: !f.active }))}>
                  {form.active ? <ToggleRight className="h-4 w-4 mr-1 text-green-600" /> : <ToggleLeft className="h-4 w-4 mr-1 text-gray-400" />}
                  {form.active ? t('servicesMgmt.active') : t('servicesMgmt.inactive')}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="service-save-btn">
              {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Catalog Tab
// ============================================================================

function CatalogTab({ businessId }: { businessId: string }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<CatalogStatus | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectForm, setConnectForm] = useState({ metaCatalogId: '', metaBusinessId: '' });
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingServices, setIsSyncingServices] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Product edit state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', priceCents: '', category: '', imageUrl: '', availability: 'in stock' });
  const [isSavingProduct, setIsSavingProduct] = useState(false);

  const openCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({ name: '', description: '', priceCents: '', category: '', imageUrl: '', availability: 'in stock' });
    setProductDialogOpen(true);
  };

  const openEditProduct = (p: Product) => {
    setEditingProduct(p);
    setProductForm({
      name: p.name,
      description: p.description || '',
      priceCents: String(p.priceCents),
      category: p.category || '',
      imageUrl: p.imageUrl || '',
      availability: p.availability,
    });
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name) return;
    setIsSavingProduct(true);
    try {
      const url = editingProduct
        ? `${API_URL}/api/operator/catalog/products/${editingProduct.id}?businessId=${businessId}`
        : `${API_URL}/api/operator/catalog/products`;
      const body = editingProduct
        ? { name: productForm.name, description: productForm.description || null, priceCents: parseInt(productForm.priceCents) || 0, category: productForm.category || null, imageUrl: productForm.imageUrl || null, availability: productForm.availability }
        : { businessId, name: productForm.name, description: productForm.description || null, priceCents: parseInt(productForm.priceCents) || 0, category: productForm.category || null, imageUrl: productForm.imageUrl || null, availability: productForm.availability, type: 'PRODUCT' };
      const res = await fetch(url, {
        method: editingProduct ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setProductDialogOpen(false);
      await fetchProducts();
    } catch (err) {
      setError('Failed to save product');
      console.error(err);
    } finally {
      setIsSavingProduct(false);
    }
  };

  const toggleProductAvailability = async (p: Product) => {
    const newAvail = p.availability === 'in stock' ? 'out of stock' : 'in stock';
    try {
      const res = await fetch(`${API_URL}/api/operator/catalog/products/${p.id}?businessId=${businessId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: getHeaders(),
        body: JSON.stringify({ availability: newAvail }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      await fetchProducts();
    } catch (err) {
      setError('Failed to update product');
      console.error(err);
    }
  };

  const filteredProducts = products.filter(
    (p: any) => typeFilter === 'all' || p.type === typeFilter.toUpperCase()
  );

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/operator/catalog?businessId=${businessId}`,
        { credentials: 'include', headers: getHeaders() },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError('Failed to load catalog status');
      console.error('Catalog status error:', err);
    }
  }, [businessId]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ businessId, limit: '50' });
      if (searchQuery) params.set('search', searchQuery);
      const res = await fetch(
        `${API_URL}/api/operator/catalog/products?${params}`,
        { credentials: 'include', headers: getHeaders() },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setProducts(data.data?.products || []);
      setTotalCount(data.data?.totalCount || 0);
    } catch (err) {
      console.error('Products fetch error:', err);
    }
  }, [businessId, searchQuery]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchStatus(), fetchProducts()]).finally(() => setIsLoading(false));
  }, [fetchStatus, fetchProducts]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/operator/catalog`, {
        method: 'POST', credentials: 'include', headers: getHeaders(),
        body: JSON.stringify({
          businessId,
          metaCatalogId: connectForm.metaCatalogId,
          metaBusinessId: connectForm.metaBusinessId,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setConnectOpen(false);
      setConnectForm({ metaCatalogId: '', metaBusinessId: '' });
      await fetchStatus();
      await fetchProducts();
    } catch (err) {
      setError('Failed to connect catalog');
      console.error(err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('catalog.confirmDisconnect'))) return;
    try {
      const res = await fetch(
        `${API_URL}/api/operator/catalog?businessId=${businessId}`,
        { method: 'DELETE', credentials: 'include', headers: getHeaders() },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      setProducts([]);
      setTotalCount(0);
      await fetchStatus();
    } catch (err) {
      setError('Failed to disconnect catalog');
      console.error(err);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/operator/catalog/sync`, {
        method: 'POST', credentials: 'include', headers: getHeaders(),
        body: JSON.stringify({ businessId }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      await fetchStatus();
      await fetchProducts();
    } catch (err) {
      setError('Failed to sync products');
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncServices = async () => {
    setIsSyncingServices(true);
    try {
      const res = await fetch(`${API_URL}/api/operator/catalog/products/sync-services?businessId=${businessId}`, {
        method: 'POST', credentials: 'include', headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const count = data.synced ?? data.count ?? 0;
      setImportResult({ imported: count, errors: 0 });
      await fetchStatus();
      await fetchProducts();
    } catch (err) {
      setError('Failed to sync services to catalog');
      console.error(err);
    } finally {
      setIsSyncingServices(false);
    }
  };

  const handleImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setIsImporting(true);
      setImportResult(null);
      try {
        const text = await file.text();
        const csvContent = btoa(new TextEncoder().encode(text).reduce((s, b) => s + String.fromCharCode(b), ''));
        const res = await fetch(`${API_URL}/api/operator/catalog/import`, {
          method: 'POST', credentials: 'include', headers: getHeaders(),
          body: JSON.stringify({ businessId, csvContent }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setImportResult({ imported: data.stats?.imported || 0, errors: data.stats?.errors || 0 });
        await fetchStatus();
        await fetchProducts();
      } catch (err) {
        setError('Failed to import CSV');
        console.error(err);
      } finally {
        setIsImporting(false);
      }
    };
    input.click();
  };

  const connected = status?.connected ?? false;
  const catalog = status?.catalog;

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-md text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}

      {importResult && (
        <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-md text-sm">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {t('catalog.importSuccess', { count: importResult.imported })} imported.
          {importResult.errors > 0 && ` ${importResult.errors} errors.`}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setImportResult(null)}>Dismiss</Button>
        </div>
      )}

      {/* Connection Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {connected ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                )}
                {connected
                  ? t('catalog.connected')
                  : t('catalog.notConnected')}
              </CardTitle>
              <CardDescription>
                {connected
                  ? `Meta Catalog ${catalog?.metaCatalogId || ''}`
                  : t('catalog.noProductsDesc')}
              </CardDescription>
            </div>
            {connected && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSync} disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  {t('catalog.syncProducts')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleSyncServices} disabled={isSyncingServices}>
                  {isSyncingServices ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Clock className="h-4 w-4 mr-1" />}
                  {t('catalog.syncServices')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleImport} disabled={isImporting}>
                  {isImporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                  {t('catalog.importCsv')}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        {connected && catalog ? (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground block">{t('catalog.columns.syncStatus')}</span>
                <Badge variant="outline" className="mt-1">{catalog.syncStatus}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground block">{t('catalog.productCount', { count: catalog.productCount })}</span>
                <span className="font-medium text-lg">{catalog.productCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">{t('catalog.syncStatus')}</span>
                <span>{catalog.lastSyncAt ? new Date(catalog.lastSyncAt).toLocaleDateString() : '\u2014'}</span>
              </div>
              <div className="flex items-end">
                <Button
                  variant="ghost" size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDisconnect}
                >
                  <Unlink className="h-4 w-4 mr-1" />
                  {t('catalog.disconnect')}
                </Button>
              </div>
            </div>
          </CardContent>
        ) : (
          <CardContent>
            <Button onClick={() => setConnectOpen(true)}>
              <Link2 className="h-4 w-4 mr-2" />
              {t('catalog.connect')}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              {t('catalog.productCount', { count: totalCount })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('catalog.searchProducts')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="catalog-search"
                />
              </div>
              <Button size="sm" onClick={openCreateProduct}>
                <Plus className="h-4 w-4 mr-1" />
                {t('catalog.newProduct')}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3" data-testid="catalog-type-filter">
            {[
              { key: 'all', label: t('catalog.filterAll') },
              { key: 'service', label: t('catalog.filterServices') },
              { key: 'product', label: t('catalog.filterProducts') },
              { key: 'combo', label: t('catalog.filterCombos') },
            ].map((tab) => (
              <Button
                key={tab.key}
                variant={typeFilter === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Package className="h-10 w-10 mb-3 opacity-40" />
              <p className="font-medium">{t('catalog.noProducts')}</p>
              <p className="text-sm mt-1">
                {t('catalog.noProductsDesc')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="catalog-products-table">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.name')}</th>
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.type')}</th>
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.sku')}</th>
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.price')}</th>
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.category')}</th>
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.availability')}</th>
                    <th className="py-2 pr-4 font-medium">{t('catalog.columns.syncStatus')}</th>
                    <th className="py-2 font-medium text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p: any) => {
                    const avail = availabilityBadge(p.availability);
                    const sync = syncBadge(p.syncStatus);
                    const SyncIcon = sync.icon;
                    const typeLabel = p.type === 'SERVICE' ? t('catalog.typeService')
                      : p.type === 'COMBO' ? t('catalog.typeCombo')
                      : t('catalog.typeProduct');
                    const typeBg = p.type === 'SERVICE' ? 'bg-blue-100 text-blue-800'
                      : p.type === 'COMBO' ? 'bg-purple-100 text-purple-800'
                      : 'bg-green-100 text-green-800';
                    return (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="py-3 pr-4">
                          <div>
                            <span className="font-medium">{p.name}</span>
                            {p.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[250px]">{p.description}</p>
                            )}
                            {p.service && (
                              <p className="text-xs text-blue-600">{p.service.durationMinutes} min{p.service.bufferMinutes ? ` + ${p.service.bufferMinutes} min buffer` : ''}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeBg}`}>
                            {typeLabel}
                          </span>
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">{p.sku}</td>
                        <td className="py-3 pr-4 font-medium">{p.price}</td>
                        <td className="py-3 pr-4">
                          {p.category ? (
                            <Badge variant="secondary">{p.category}</Badge>
                          ) : '\u2014'}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant={avail.variant} className={avail.className}>
                            {p.availability}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <SyncIcon className={`h-4 w-4 ${sync.className}`} />
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditProduct(p)} title={t('common.edit')}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => toggleProductAvailability(p)}
                              title={p.availability === 'in stock' ? t('catalog.markOutOfStock') : t('catalog.markInStock')}
                            >
                              {p.availability === 'in stock' ? <ToggleRight className="h-3.5 w-3.5 text-green-600" /> : <ToggleLeft className="h-3.5 w-3.5 text-gray-400" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Create/Edit Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProduct ? t('catalog.editProduct') : t('catalog.newProduct')}</DialogTitle>
            <DialogDescription>{editingProduct ? t('catalog.editProductDesc') : t('catalog.createProductDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{t('catalog.columns.name')} *</Label>
              <Input value={productForm.name} onChange={(e) => setProductForm(f => ({ ...f, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>{t('catalog.description')}</Label>
              <Input value={productForm.description} onChange={(e) => setProductForm(f => ({ ...f, description: e.target.value }))} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('catalog.columns.price')} (centavos)</Label>
                <Input type="number" value={productForm.priceCents} onChange={(e) => setProductForm(f => ({ ...f, priceCents: e.target.value }))} className="mt-1" />
                <p className="text-xs text-muted-foreground mt-1">{productForm.priceCents ? formatPrice(parseInt(productForm.priceCents) || 0) : 'R$ 0,00'}</p>
              </div>
              <div>
                <Label>{t('catalog.columns.category')}</Label>
                <Input value={productForm.category} onChange={(e) => setProductForm(f => ({ ...f, category: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>{t('catalog.imageUrl')}</Label>
              <Input value={productForm.imageUrl} onChange={(e) => setProductForm(f => ({ ...f, imageUrl: e.target.value }))} placeholder="https://..." className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveProduct} disabled={isSavingProduct || !productForm.name}>
              {isSavingProduct && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editingProduct ? t('common.save') : t('catalog.createProduct')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connect Dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('catalog.connectDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('catalog.connectDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{t('catalog.connectDialog.catalogId')}</Label>
              <Input
                value={connectForm.metaCatalogId}
                onChange={(e) => setConnectForm((f) => ({ ...f, metaCatalogId: e.target.value }))}
                placeholder="e.g. 123456789"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('catalog.connectDialog.catalogIdHint')}
              </p>
            </div>
            <div>
              <Label>{t('catalog.connectDialog.businessId')}</Label>
              <Input
                value={connectForm.metaBusinessId}
                onChange={(e) => setConnectForm((f) => ({ ...f, metaBusinessId: e.target.value }))}
                placeholder="e.g. 987654321"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('catalog.connectDialog.businessIdHint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConnect}
              disabled={isConnecting || !connectForm.metaCatalogId || !connectForm.metaBusinessId}
            >
              {isConnecting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {t('catalog.connect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function ProductsAndServicesPage() {
  const { t } = useTranslation();
  const { selectedBusiness } = useOperator();
  const businessId = selectedBusiness?.id;

  if (!businessId) {
    return (
      <div className="p-6" data-testid="catalog-page">
        <h1 className="text-2xl font-bold mb-2">{t('nav.productsAndServices')}</h1>
        <p className="text-muted-foreground">{t('common.selectBusinessFirst')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="catalog-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="h-6 w-6" />
          {t('nav.productsAndServices')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('catalog.mergedDescription')}
        </p>
      </div>

      <Tabs defaultValue="services">
        <TabsList>
          <TabsTrigger value="services" className="gap-1.5">
            <Scissors className="h-4 w-4" />
            {t('servicesMgmt.title')}
          </TabsTrigger>
          <TabsTrigger value="catalog" className="gap-1.5">
            <ShoppingBag className="h-4 w-4" />
            {t('catalog.title')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services">
          <ServicesTab businessId={businessId} />
        </TabsContent>

        <TabsContent value="catalog">
          <CatalogTab businessId={businessId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ProductsAndServicesPage;
